/**
 * Mounts the toast stack and hands `showToast` to everything below it
 * (BON-472). One instance, in `App`'s authed tree.
 *
 * The live region is rendered ALWAYS, empty or not: a screen reader only
 * announces changes inside a region that already existed when the change
 * happened, so a container mounted at the same moment as its first toast
 * announces nothing.
 *
 * `polite` rather than `assertive` because these are confirmations — interrupting
 * whatever the CSR is reading to say "the link went" is worse than telling them a
 * second later. Anything urgent belongs in a dialog, which takes focus.
 */
import { useCallback, useMemo, useRef, useState } from 'react';

import { ToastContext, type Toast, type ToastApi, type ToastRequest } from './toastContext';
import { ToastItem } from './ToastItem';

/** Beyond this the stack stops being readable and starts covering the page. The
 *  OLDEST goes, because the newest is the one the CSR just caused. */
const MAX_VISIBLE = 3;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // A counter rather than a timestamp: two toasts fired in the same tick would
  // collide on `Date.now()` and React would reuse one of their keys.
  const nextId = useRef(1);

  const dismissToast = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((request: ToastRequest) => {
    const toast: Toast = { ...request, id: nextId.current++ };
    setToasts((current) => [...current, toast].slice(-MAX_VISIBLE));
  }, []);

  const api = useMemo<ToastApi>(() => ({ showToast, dismissToast }), [showToast, dismissToast]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 right-4 z-[100] flex flex-col gap-2"
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={() => dismissToast(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
